import React, { useState, useEffect } from 'react';
import './BookingCalendar.scss';
import {
  IonPage,
  IonContent,
  IonButton,
  IonBadge,
  IonAlert,
  IonIcon,
  IonText,
  IonRow,
  IonCol,
  IonLoading,
  IonSpinner
} from '@ionic/react';
import { AppointmentStepProps } from '../../navigators/AppointmentNavigator';
import Stepper from '../../components/Stepper';
import AppHeader from '../../components/AppHeader';
import { alertCircleOutline } from 'ionicons/icons';
import { Appointment } from '../../models';
import { setAppointment } from '../../store/actions/appointments';
import { connect } from 'react-redux';
import { AppointmentsRootState } from '../../store/reducers/appointments';
import LaboratoryRepository from '../../repositories/LaboratoryRepository';
import SlotModel from '../../models/Slot';
import groupBy from '../../utils/groupBy';
import capitalize from '../../utils/capitalize';
import AccordionItem from '../../components/AccordionItem';
import { FeatureToggle, On } from '../../components/FeatureToggle';

type CalendarSlotProps = {
  datetime: Date;
  onSelectSlot: (datetime: Date, info: string | undefined) => any;
  info?: string;
};

const CalendarSlot: React.FC<CalendarSlotProps> = ({
  datetime,
  onSelectSlot,
  info
}) => (
  <li className="calendar-week__slot">
    <IonButton
      onClick={(e) => onSelectSlot(datetime, info)}
      className={info ? 'ion-button-no-padding-right' : ''}
    >
      {datetime.toLocaleTimeString('fr-FR', {
        hour: 'numeric',
        minute: 'numeric'
      })}
      {info ? (
        <IonBadge color="light" className="calendar-week__badge">
          !{' '}
        </IonBadge>
      ) : null}
    </IonButton>
  </li>
);

type Slot = {
  datetime: Date | false;
  info?: string;
};

type CalendarDayProps = {
  date: Date;
  slots: Array<SlotModel>;
  open?: boolean;
  onSelectSlot: (slot: SlotModel) => any;
};

const CalendarDay: React.FC<CalendarDayProps> = ({
  date,
  slots,
  open = false,
  onSelectSlot
}) => {
  return (
    <div className="calendar-day">
      <AccordionItem
        name={capitalize(
          date.toLocaleDateString('fr-FR', {
            weekday: 'long',
            month: 'short',
            day: 'numeric'
          })
        )}
        open={open}
      >
        <ul className="calendar-week__slot-list">
          {slots.map((slot) =>
            slot.remaining > 0 ? (
              <CalendarSlot
                datetime={slot.start}
                onSelectSlot={() => onSelectSlot(slot)}
                info={slot.message}
                key={slot.id}
              />
            ) : null
          )}
        </ul>
      </AccordionItem>
    </div>
  );
};

const mapStateToProps = (
  state: { appointments: AppointmentsRootState },
  ownProps: any
) => {
  return {
    laboratory: state.appointments.appointment.laboratory
  };
};

const mapDispatchToProps = (dispatch: Function, ownProps: any) => ({
  updateAppointment: (appointment: Partial<Appointment>) =>
    dispatch(setAppointment(appointment))
});

type BookingCalendarProps = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps> &
  AppointmentStepProps;

const BookingCalendar: React.FC<BookingCalendarProps> = ({
  step,
  history,
  updateAppointment,
  nextStepRouterLink = '/',
  laboratory
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isMoreSlotsLoading, setIsMoreSlotsLoading] = useState(false);
  const [hasMoreSlots, setHasMoreSlots] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState<SlotModel>();
  const [showConfirmationAlert, setShowConfirmationAlert] = useState(false);
  const [days, setDays] = useState<Array<any>>([]);

  // TODO: Manage this message through back-office lab property
  const { id: labId = '' } = laboratory || {};
  const labsIdsWithNoMessageInSlotSelectionScreen = (
    process.env.REACT_APP_LAB_IDS_WITH_NO_MESSAGE_IN_SLOT_SELECTION_SCREEN || ''
  )
    .split(',')
    .map((i) => i.trim());
  const shouldDisplayMessage =
    labsIdsWithNoMessageInSlotSelectionScreen.indexOf(String(labId)) === -1;

  useEffect(() => {
    async function fetchSlots() {
      if (laboratory) {
        setIsLoading(true);
        const slots = await LaboratoryRepository.getSlotsByLaboratory(
          laboratory
        );

        const groupedSlots = groupSlots(slots);

        setDays(groupedSlots);
        setIsLoading(false);
      }
    }

    fetchSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laboratory]);

  function groupSlots(slots: SlotModel[]) {
    let slotsEntity = slots.map((slot) => SlotModel.createFromEntity(slot));

    let groupedSlots = Array.from(
      groupBy(slotsEntity, (slot: SlotModel) => {
        return slot.start.toDateString();
      })
    ).map((day: any) => {
      return {
        datetime: new Date(day[0]),
        slots: day[1]
      };
    });

    return groupedSlots;
  }

  async function loadMoreSlots() {
    if (laboratory) {
      setIsMoreSlotsLoading(true);

      var nextDate = new Date(days[days.length - 1].datetime);
      nextDate.setDate(nextDate.getDate() + 1); // Get last slot and add one day

      const slots = await LaboratoryRepository.getSlotsByLaboratory(
        laboratory,
        nextDate
      );

      if (slots.length) {
        const groupedSlots = groupSlots(slots);

        setDays([...days, ...groupedSlots]);
      }

      setHasMoreSlots(!!slots.length);

      setIsMoreSlotsLoading(false);
    }
  }

  function selectSlot(slot: SlotModel) {
    setSelectedSlot(slot);
    setShowConfirmationAlert(true);
  }

  function confirmSlot() {
    if (selectedSlot && selectedSlot.id) {
      updateAppointment({
        slot: selectedSlot
      });
      history.push(nextStepRouterLink);
    }
  }

  return (
    <IonPage>
      <IonLoading isOpen={isLoading} />
      <AppHeader />
      <Stepper step={step} />
      <IonContent>
        <div className="wrapper wrapper--stretch wrapper--centered-md">
          <div className="ion-padding">
            <FeatureToggle active={shouldDisplayMessage}>
              <On>
                <div className="booking-calendar__info">
                  <IonText color="secondary">
                    <IonRow className="ion-align-items-center">
                      <IonCol size="auto">
                        <IonIcon size="large" icon={alertCircleOutline} />
                      </IonCol>
                      <IonCol>
                        En cas d'indisponibilité de créneaux, votre laboratoire
                        continue de vous accueillir sans rendez-vous sur les
                        horaires d'ouverture
                      </IonCol>
                    </IonRow>
                  </IonText>
                </div>
              </On>
            </FeatureToggle>
          </div>
          <div>
            {!isLoading ? (
              days.length ? (
                <>
                  <div className="calendar-week">
                    {days.map((day, index) => {
                      return (
                        <div key={day.datetime.toISOString()}>
                          <CalendarDay
                            date={day.datetime}
                            slots={day.slots as Array<SlotModel>}
                            onSelectSlot={(slot) => selectSlot(slot)}
                            open={index === 0}
                          />
                        </div>
                      );
                    })}
                  </div>
                  {hasMoreSlots ? (
                    <div className="ion-text-center">
                      <IonButton
                        disabled={isMoreSlotsLoading}
                        onClick={() => loadMoreSlots()}
                      >
                        {isMoreSlotsLoading ? (
                          <IonSpinner />
                        ) : (
                          'Choisir une autre date'
                        )}
                      </IonButton>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="ion-text-center ion-padding">
                  <IonBadge color="warning">
                    Aucun horaire n'est disponible pour ce laboratoire.
                  </IonBadge>
                </div>
              )
            ) : null}
          </div>
        </div>
      </IonContent>
      <IonAlert
        cssClass={
          selectedSlot?.message ? 'ion-alert-warning-header' : undefined
        }
        isOpen={showConfirmationAlert}
        onDidDismiss={() => setShowConfirmationAlert(false)}
        header={
          selectedSlot?.message
            ? `⚠ ${selectedSlot.message}`
            : `${selectedSlot?.getStartDayString()} à ${selectedSlot?.getStartHourString()}`
        }
        subHeader={
          selectedSlot?.message
            ? `${selectedSlot?.getStartDayString()} à ${selectedSlot?.getStartHourString()}`
            : undefined
        }
        buttons={[
          {
            text: 'Annuler',
            role: 'cancel',
            handler: () => setShowConfirmationAlert(false)
          },
          {
            text: 'Confirmer',
            handler: () => confirmSlot()
          }
        ]}
      />
    </IonPage>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(BookingCalendar);
